import type { DirectoryCreateSchema } from '@repo/schemas/directory/directoryCreate';

import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { usePostDirectory } from '@repo/api/directory/usePostDirectory';
import { ErrorMessageHandler } from '@repo/lib/errorMessageHandler';
import { fsErrorMessages } from '@repo/lib/messages/fsErrorMessages';
import { directoryCreateSchema } from '@repo/schemas/directory/directoryCreate';
import { ArrowRight, Folder } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import { LoadingSwap } from '@/components/ui/loading-swap';

/**
 * Props for the CreateFSDir component.
 * @property {string} parentDirId - The parent directory ID for the new folder.
 * @property {(open: boolean) => void} onOpenChange - Callback for dialog open state.
 * @property {boolean} open - Whether the dialog is open.
 */
interface CreateFSDirProps {
  parentDirId: string;
  onOpenChange: (open: boolean) => void;
  open: boolean;
}

export default function CreateFSDir(createFSDirProps: CreateFSDirProps) {
  const { parentDirId, onOpenChange, open } = createFSDirProps;
  const { mutateAsync, isPending } = usePostDirectory();

  const form = useForm<DirectoryCreateSchema>({
    mode: 'onBlur',
    defaultValues: {
      name: 'Nowy Folder',
      parentDirId: parentDirId,
    },
    resolver: standardSchemaResolver(directoryCreateSchema),
  });

  const onClose = () => {
    form.reset();
    onOpenChange(false);
  };

  const onSubmit = (data: DirectoryCreateSchema) => {
    mutateAsync(data)
      .then(() => {
        onOpenChange(false);
      })
      .catch((error) => {
        const errorHandler = new ErrorMessageHandler(fsErrorMessages);
        const { title } = errorHandler.processError(error);
        form.setError('name', { type: 'custom', message: title });
      });
  };

  useEffect(() => {
    if (open) {
      form.reset({
        name: 'Nowy Folder',
        parentDirId: parentDirId,
      });
    }
  }, [form, open, parentDirId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={'sm:max-w-108'} onCloseAutoFocus={onClose} onEscapeKeyDown={onClose}>
        <DialogDescription className={'sr-only'}>Formularz do tworzenia folderu</DialogDescription>
        <Form {...form}>
          <DialogBody>
            <DialogHeader>
              <DialogTitle>Nowy Folder</DialogTitle>
            </DialogHeader>
            <form className={'flex grow flex-col gap-3 overflow-auto p-4'} onSubmit={form.handleSubmit(onSubmit)} noValidate>
              <FormField
                name={'name'}
                control={form.control}
                render={({ field }) => (
                  <FormItem className={'col-span-1'}>
                    <FormControl>
                      <InputGroup>
                        <InputGroupInput {...field} placeholder={'Nazwa'} />
                        <InputGroupAddon align="inline-start">
                          <Folder className="text-muted-foreground" />
                        </InputGroupAddon>
                      </InputGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </form>
          </DialogBody>
          <DialogFooter>
            <Button onClick={onClose} type={'button'} variant={'tertiary'} className={'w-full md:w-fit'}>
              Anuluj
            </Button>
            <Button disabled={isPending} onClick={form.handleSubmit(onSubmit)} type={'submit'} variant={'primary'} className={'w-full md:w-fit'}>
              <LoadingSwap isLoading={isPending} className="flex items-center gap-2">
                Utwórz
                <ArrowRight className={'transition-transform group-hover:translate-x-0.5'} />
              </LoadingSwap>
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
